




import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {Chart} from 'highcharts-vue';
import More from 'highcharts/highcharts-more';
import Highcharts from 'highcharts';
import {colors} from '@/config';
import vue from 'vue';

More(Highcharts);

@Component({components: {Chart}})
export default class SustainabilityCompass extends Vue {
  @Prop() readonly sustainability!: any;

  component: any = undefined;

  chartOptions: any = {
    chart: {
      polar: true,
      type: 'column',
      backgroundColor: 'transparent',
    },

    colors: [colors.DarkGrey, colors.Primary, colors.Secondary],

    title: {
      text: '',
      align: 'center',
      style: {
        fontSize: '1.75rem',
        color: colors.Black,
      },
    },

    pane: {
      size: '90%',
      background: {
        backgroundColor: colors.Light,
        outerRadius: '100%',
      },
    },
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      itemStyle: {
        fontSize: '1rem',
        fontWeight: 'normal',
        color: colors.Black,
      },
      itemDistance: 50,
      margin: 50,
    },
    xAxis: {
      tickmarkPlacement: 'on',
      categories: [],
      labels: {
        style: {
          fontSize: '1rem',
          color: colors.Black,
        },
        distance: 25,
      },
      lineColor: colors.LightGrey,
      gridLineColor: colors.LightGrey,
    },
    yAxis: {
      min: 2,
      max: 100,
      type: 'logarithmic',
      endOnTick: false,
      showLastLabel: true,
      labels: {
        enabled: false,
      },
      gridLineColor: colors.LightGrey,
      gridLineDashStyle: 'Solid',
      reversedStacks: false,
    },
    series: [],
    tooltip: {
      headerFormat: '<b>{point.key}</b><br>',
      pointFormat: Vue.prototype.$t('Påverkan') + (': <b>{series.name}</b><br> Index: {point.y:.1f}/100'),
      valueSuffix: '/100',
      backgroundColor: colors.Light,
      padding: 10,
      style: {
        fontSize: '1.25rem',
      },
      useHTML: true,
      distance: 10,
    },
    plotOptions: {
      series: {
        stacking: 'normal',
        groupPadding: 0,
        grouping: false,
        states: {
          inactive: {
            opacity: 1,
          },
          hover: {
            shadow: true,
            color: colors.Tertiary,
            borderWidth: '2px',
            borderColor: colors.Primary,
          },
        },
        point: {
          events: {
            mouseOver: this.onMouseOver,
          },
        },
        events: {
          mouseOut: this.onMouseOut,
        },
      },
    },
    exporting: {
      enabled: false,
    },
    credits: {
      text: '\u00A9 Bolagskompassen AB',
      href: 'https://bolagskompassen.se',
      style: {
        color: colors.LightGrey,
        fontSize: '0.75rem',
      },
      position: {
        x: -10,
        y: -10,
      },
    },
  };

  onMouseOver(e: any) {
    const i = e.target.index;
    const series = this.chartOptions.series;
    const res = {
      group: this.sustainability.Groups[i].Name,
      value: this.sustainability.Groups[i].Value,
      title: e.target.category,
    };
    this.component.$emit('hover', res);
  }

  onMouseOut() {
    this.component.$emit('hover', null);
  }

  created() {
    this.component = this;
  }

  @Watch('sustainability', {immediate: true, deep: true})
  onSustainabilityChanged() {
    if (!this.sustainability) {
      return;
    }

    let categories: string[] = [];
    let s1: Array<number | null> = [];
    let s2: Array<number | null> = [];
    let s3: Array<number | null> = [];

    for (const group of this.sustainability.Groups) {
      categories = categories.concat(this.title(group.Name));
      s1 = s1.concat(this.val(group.Value, 0.015, 0.04));
      s2 = s2.concat(this.val(group.Value, 0.04, 0.2));
      s3 = s3.concat(this.val(group.Value, 0.2, 1));
    }

    this.chartOptions.xAxis.categories = categories;
    this.chartOptions.series = [{
      name: Vue.prototype.$t('Begränsad'),
      type: 'column',
      data: s1,
    },
      {
        name: Vue.prototype.$t('Påtaglig'),
        type: 'column',
        data: s2,
      },
      {
        name: Vue.prototype.$t('Betydande'),
        type: 'column',
        data: s3,
      },
    ];
  }

  group(name: string): any | null {
    if (!this.sustainability) {
      return null;
    }
    for (const g of this.sustainability.Groups) {
      if (g.Name === name) {
        return g;
      }
    }
    return null;
  }

  val(val: number, lower: number, upper: number): number | null {
    if (val >= lower && val < upper) {
      return val * 100;
    }
    return null;
  }

  title(group: string): string {
    switch (group) {
      case '40':
        return Vue.prototype.$t('Varor & material');
      case '46':
        return Vue.prototype.$t('Underentreprenörer');
      case '50':
        return Vue.prototype.$t('Lokaler');
      case '51':
        return Vue.prototype.$t('Fastighetskostnader');
      case '52':
        return Vue.prototype.$t('Hyra av anläggningstillgångar');
      case '53':
        return Vue.prototype.$t('Energi');
      case '54':
        return Vue.prototype.$t('Förbrukningsmaterial');
      case '55':
        return Vue.prototype.$t('Reparation & underhåll');
      case '56':
        return Vue.prototype.$t('Transportmedel');
      case '57':
        return Vue.prototype.$t('Frakter & transport');
      case '58':
        return Vue.prototype.$t('Resor');
      case '59':
        return Vue.prototype.$t('Reklam & PR');
      case '60':
        return Vue.prototype.$t('Övriga försäljningskostnader');
      case '61':
        return Vue.prototype.$t('Kontorsmaterial');
      case '62':
        return Vue.prototype.$t('Tele & post');
      case '63':
        return Vue.prototype.$t('Försäkringar & riskhantering');
      case '64':
        return Vue.prototype.$t('Förvaltning');
      case '65':
        return Vue.prototype.$t('Övriga externa tjänster');
      case '68':
        return Vue.prototype.$t('Inhyrd personal');
      case '69':
        return Vue.prototype.$t('Övriga externa kostnader');
      case '70':
        return Vue.prototype.$t('Medarbetare');
      case '74':
        return Vue.prototype.$t('Pensioner');
      case '75':
        return Vue.prototype.$t('Skatter');
    }
    return '';
  }

}
